import { useState, useRef } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";

//components
import DataView from 'Components/DataView';
import ChipStatus from 'Components/ChipStatus';
import { Button } from "@gpstrackit/react-controls";
import { NotificationManager } from 'Components/Notifications';

//Views
import CreateEditDeviceModel from 'Modules/manufacturers/.components/deviceModelsTab/.components/createEditDeviceModel';
import BulkAssignAlertTypes from 'Modules/manufacturers/.components/deviceModelsTab/.components/bulkAssignAlertTypes';
//Helpers
import { idsValidation } from 'Core/data/Helpers';
//Permission
import { HasPermission } from "Core/security";

//Actions
import { deviceModelCreateEditOpenModal, bulkAssignAlertTypesOpenModal } from 'Redux/actions';

//Styles
import './index.scss';

const DevicesModelsTab = (props) => {
    const { height, manufacturer } = props;
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    //Redux States
    const user = useSelector(state => state.securityRedux.user);
    const openModalCreateDeviceModel = useSelector(state => state.createEditDeviceModelRedux.openModal);
    const openModalBulkAssignAlertTypes = useSelector(state => state.bulkAssignAlertTypesRedux.openModal);
    const lang = useSelector(state => state.commonRedux.language);

    const [columns, setColumns] = useState([
        {
            key: "name",
            title: messages['devicemodel_tab_name'],
            field: "name",
            show: true,
            resize: true,
            width: 250,
            sort: true,
            export: true,
            functionString: 'return row?.name',
            code: "devicemodel_tab_name",
            render: (item) => {
                return <span>{item?.name}</span>
            }
        },
        {
            key: "product",
            field: "product",
            title: messages['devicemodel_tab_product'],
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.product',
            code: 'devicemodel_tab_product',
            render: (item) => {
                return <span>{item?.product}</span>
            }
        },
        {
            key: "inputs",
            title: messages['devicemodel_tab_inputs'],
            field: "input",
            show: true,
            resize: true,
            width: 100,
            export: true,
            functionString: 'return row?.input',
            code: "devicemodel_tab_inputs",
            render: (item) => {
                return <span>{item?.input}</span>
            }
        },
        {
            key: "Outputs",
            title: messages['devicemodel_tab_outputs'],
            field: "output",
            show: true,
            resize: true,
            width: 100,
            export: true,
            functionString: 'return row?.output',
            code: "devicemodel_tab_outputs",
            render: (item) => {
                return <span>{item?.output}</span>
            }
        },
        {
            key: "unitType_name",
            title: messages['devicemodel_tab_unit_type'],
            field: "unitType { name }",
            show: true,
            resize: true,
            width: 250,
            export: true,
            functionString: 'return row?.unitType?.name',
            code: "devicemodel_tab_unit_type",
            render: (item) => {
                return <span>{item?.unitType?.name}</span>
            }
        },
        {
            key: "suportGeofences",
            title: messages['devicemodel_tab_support_geofences'],
            field: "deviceModelGeofence { maxGeofence }",
            show: true,
            resize: true,
            width: 150,
            export: true,
            functionString: 'return row?.deviceModelGeofence?.maxGeofence > 0 ? "Yes" : "No"',
            code: "devicemodel_tab_support_geofences",
            render: (item) => {
                return <span>{item?.deviceModelGeofence?.maxGeofence > 0 ? messages['common_yes'] : messages['common_no']}</span>
            }
        },
        {
            key: "geofence",
            title: messages['devicemodel_tab_geofence_type'],
            field: "deviceModelGeofence { geofence }",
            show: true,
            resize: true,
            width: 150,
            export: true,
            functionString: 'return row?.deviceModelGeofence?.maxGeofence > 0 ? row?.deviceModelGeofence?.geofence === 2 ? "Both" : row?.deviceModelGeofence?.geofence === 1 ? "Polygon" : "Regular" : "NA"',
            code: "devicemodel_tab_geofence_type",
            render: (item) => {
                return <span>{item?.deviceModelGeofence?.maxGeofence > 0 ? item?.deviceModelGeofence?.geofence === 2 ? messages['devicemodel_tab_geofence_both'] : item?.deviceModelGeofence?.geofence === 1 ? messages['devicemodel_tab_geofence_polygon'] : messages['devicemodel_tab_geofence_regular_shape'] : "NA"}</span>
            }
        },
        {
            key: "status",
            title: messages['devicemodel_tab_status'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            functionString: `
                return "Active"
            `,
            code: "devicemodel_tab_status",
            render: (item) => {
                return <ChipStatus messages={messages} statusCode={1} />
            }
        },
    ])

    return (
        <div style={{ marginTop: 10 }}>
            <DataView
                ref={tableRef}
                columns={columns}
                dinamicQuery
                name={`pm-devicemodel-tab-${manufacturer?.name}`}
                exportName="devicemodels"
                entity="device-models-tab-v1"
                graphqlFunction="searchDeviceModels"
                graphqlSort="DeviceModelSortInput"
                graphqlConditions="DeviceModelSearchInput"
                customQuery="id"
                hideAllInPagination
                //search
                asyncSelect
                searchConditions={["name"]}
                searchOptionValue="id"
                searchOptionLabel="name"
                customSearch={{ fields: ["manufacturer_name"], terms: [manufacturer?.name], exact: true }}
                onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                    let currentConditions = {}
                    let currentTags = {}
                    if (!conditions && tags) {
                        currentConditions = tableRef?.current?.conditions;
                        currentTags = tags;
                    } else {
                        currentConditions = conditions || [];
                        currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                        if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                    }

                    currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'manufacturer_name');

                    let tagsById = currentTags.filter((item) => typeof item.id === "number");
                    if (tagsById?.length > 0) {
                        //New Items
                        let currentIds = tagsById.map((item) => (item.id));
                        if (currentIds.length > 0) {
                            currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                        }
                    }
                    let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                    if (tagsByLabel.length > 0) {
                        let currentLabels = tagsByLabel.map((item) => (item.name))
                        if (currentLabels.length > 0) {
                            currentConditions.push({ fields: ["name"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                        }
                    }

                    currentConditions.push({ fields: ["manufacturer_name"], terms: [manufacturer?.name], exact: true, conditionOperator: "AND" });
                    if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                    else tableRef?.current?.getData(null, rows, sort, currentConditions);
                }}
                searchFields={["name"]}
                noSplit
                onChangeColumns={(items) => {
                    let currentColumns = items.map((item) => ({
                        ...item,
                        title: item.code ? messages[item.code] : item.title,
                        render: item.key === "status" ? (item) => {
                            return <ChipStatus statusCode={1} messages={messages} />
                        } : item.render
                    }))
                    setColumns(currentColumns)
                }}
                tableButtons={["export", 'orderColumn']}
                noFilters
                height={height}
                menuOptions={() => {
                    return [
                        messages['edit']
                    ]
                }}
                options={[
                    {
                        id: "edit-device-model",
                        label: messages['edit'],
                        show: true,
                        disabled: !HasPermission(user, "devmod", "u"),
                        single: true,
                        action: (items) => {
                            dispatch(deviceModelCreateEditOpenModal(items[0]));
                        }
                    },
                    {
                        id: "bulk-alert-type-device-model",
                        label: messages['devicemodel_tab_bulk_assign_alert_types'],
                        show: true,
                        disabled: !HasPermission(user, "bdvmoaltt", "u"),
                        multiple: true,
                        action: (items) => {
                            dispatch(bulkAssignAlertTypesOpenModal(true, items))
                        }
                    }
                ]}
                onClickMenu={(event, item) => {
                    if (event === messages['edit']) {
                        if (HasPermission(user, "devmod", "u")) {
                            dispatch(deviceModelCreateEditOpenModal(item))
                        } else {
                            NotificationManager.warning(
                                messages['common_error_permission'],
                                messages['edit'],
                                3000,
                            );
                        }
                    }

                }}
                extraButton={() => {
                    return (
                        <div style={{ paddingLeft: 2 }}>
                            <Button
                                onClick={() => dispatch(deviceModelCreateEditOpenModal())}
                                className="btn-dark-blue"
                                disabled={!HasPermission(user, "devmod", "c")}
                                style={{ height: 42 }}
                            >
                                {messages['devicemodel_tab_add_new']}
                            </Button>
                        </div>

                    )
                }}
            />
            {openModalCreateDeviceModel && <CreateEditDeviceModel manufacturer={manufacturer} tableRef={tableRef} />}
            {openModalBulkAssignAlertTypes && <BulkAssignAlertTypes />}
        </div>
    )
}

export default DevicesModelsTab